import moment from 'moment';
import { APPFOLIO_PUSH_STATUS, getLiveAttempt, PUSHABLE_STATUSES, resolveApPushInvoiceNumber } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { DASH, pickValue } from './BillDetails/billDetailsUtils';

/** Where a hand-typed invoice number can be found on an edited payload. */
const TYPED_INVOICE_KEYS = ['invoiceNumber', 'invoiceNo'];

/**
 * What the AP push should file this bill under, judged on the **pristine extraction**.
 *
 * The split is the point. `data` is what OCR read and how confidently; `editedData` is what a person
 * typed, and the edit table is where somebody puts an invoice number the bill has none of. Weighed
 * together, a number somebody guessed at arrives wearing the extraction run's confidence — so the
 * number they just typed is discarded as redundant and the guess becomes the payable's identity,
 * while `isInvoiceNumberFixed` starts locking the field against their own typing.
 *
 * The service draws the same line on its side, for the same reason. It lives here as a function
 * rather than inline in the modal so the rule can be pinned: an edit used to last only as long as
 * the session, and now it arrives on every read, so passing `effectiveData` would be an easy and
 * entirely silent mistake to make.
 */
export function resolveBillApPushInvoiceNumber(bill) {
  return resolveApPushInvoiceNumber({
    extraction: bill === null || bill === void 0 ? void 0 : bill.data,
    typedInvoiceNumber: bill !== null && bill !== void 0 && bill.editedData ? pickValue(bill.editedData, TYPED_INVOICE_KEYS) : ''
  });
}

/** Extraction has to have reached this before the service accepts a push. */
export const VALIDATED_BILL_STATUS = 'Validated';

/** Why a bill of a utility not enrolled in AP Push is not offered for pushing (LB-6031). */
export const NOT_ENROLLED_REASON = "This utility isn't enrolled in AP Push. Turn it on in the property's utility setup.";
const CHIP_DATE_FORMAT = 'MM/DD/YY';

/**
 * How far a bill has got with AP push, from the status the service reported. Everything the table and
 * the details view decide comes from here, so the row's chip and the modal's button can never disagree
 * about the same bill.
 *
 * `pushStatus` is null both before the first read and when the read omitted the bill; `hasReadStatus`
 * is what separates them. An omitted bill belongs to another company or does not exist, and the
 * contract is explicit that it must not be shown as "not pushed".
 */
export function getAppfolioPushRowState(_ref) {
  var _liveAttempt$blockers, _pushStatus$status;
  let billExtractStatus = _ref.billExtractStatus,
    hasReadStatus = _ref.hasReadStatus,
    _ref$invoiceNumber = _ref.invoiceNumber,
    invoiceNumber = _ref$invoiceNumber === void 0 ? null : _ref$invoiceNumber,
    _ref$isEnrolled = _ref.isEnrolled,
    isEnrolled = _ref$isEnrolled === void 0 ? null : _ref$isEnrolled,
    _ref$isPushing = _ref.isPushing,
    isPushing = _ref$isPushing === void 0 ? false : _ref$isPushing,
    pushStatus = _ref.pushStatus;
  const isUnknown = hasReadStatus && !pushStatus;
  const isValidated = billExtractStatus === VALIDATED_BILL_STATUS;
  const liveAttempt = getLiveAttempt(pushStatus);
  const canPush = !isPushing && !isUnknown && isValidated && isEnrolled !== false &&
  // A document pushed from another one is settled by that link, whatever its own status says.
  !(pushStatus !== null && pushStatus !== void 0 && pushStatus.duplicateOfBillId) &&
  // A payable is identified by its invoice number; without one the service refuses the push, so
  // offering it here only produces a refusal the person was given no way to predict.
  (invoiceNumber === null || invoiceNumber === void 0 ? void 0 : invoiceNumber.canPush) !== false && (
  // No status yet is the unpushed case: nothing has ever been recorded for this document.
  !pushStatus || PUSHABLE_STATUSES.includes(pushStatus.status));

  /** Why the control is inert, for the states that do not already say so themselves. */
  const getDisabledReason = () => {
    if (canPush) return null;
    if (isPushing) return null;
    if (!isValidated) return 'The bill data has to be validated before it can be pushed to AppFolio.';
    if (isEnrolled === false) return NOT_ENROLLED_REASON;
    if (isUnknown) return 'The AppFolio push status is not available for this bill.';
    if ((invoiceNumber === null || invoiceNumber === void 0 ? void 0 : invoiceNumber.canPush) === false) return invoiceNumber.reason;
    return null;
  };
  return {
    blockers: (_liveAttempt$blockers = liveAttempt === null || liveAttempt === void 0 ? void 0 : liveAttempt.blockers) !== null && _liveAttempt$blockers !== void 0 ? _liveAttempt$blockers : [],
    canPush,
    disabledReason: getDisabledReason(),
    hasReadStatus,
    isEnrolled,
    isUnknown,
    isValidated,
    liveAttempt,
    pushStatus,
    status: (_pushStatus$status = pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) !== null && _pushStatus$status !== void 0 ? _pushStatus$status : null
  };
}

/** No push has ever been recorded for the bill. One that has keeps its status, whatever else is now true of it. */
const hasNothingRecorded = state => !state.status || state.status === APPFOLIO_PUSH_STATUS.notPushed;

/**
 * A bill that can never be pushed as it stands -- a duplicate, a rejected or failed read, one still being read -- and
 * has nothing recorded against it (LB-6031). "Not pushed" would read as AP work to do, so the column shows a dash, as
 * the Allocation column does for the same rows.
 */
const hasNoPushToShow = state => !state.isValidated && hasNothingRecorded(state);

/** Not enrolled in AP Push, so nobody means to push it: "Not pushed" would read as AP work to do (LB-6031). */
const isNotEnrolled = state => state.isEnrolled === false && hasNothingRecorded(state);

/**
 * The bills table's AP push cell. Returns null before the first read has landed, so the column does
 * not flash "Not pushed" at every row on the way to the truth.
 */
export function getAppfolioPushChipProps(state) {
  var _state$pushStatus;
  if (!state.hasReadStatus) return null;
  if (state.isUnknown) return {
    label: DASH,
    variant: 'inactive'
  };

  // A duplicate link outranks the document's own status: the invoice reached AppFolio either way.
  if ((_state$pushStatus = state.pushStatus) !== null && _state$pushStatus !== void 0 && _state$pushStatus.duplicateOfBillId) return {
    label: 'Pushed elsewhere',
    variant: 'warning'
  };
  if (hasNoPushToShow(state)) return {
    label: DASH,
    variant: 'inactive'
  };
  if (isNotEnrolled(state)) return {
    label: 'Not enrolled',
    variant: 'inactive'
  };
  switch (state.status) {
    case APPFOLIO_PUSH_STATUS.succeeded:
      {
        var _state$pushStatus2;
        const pushedOn = (_state$pushStatus2 = state.pushStatus) !== null && _state$pushStatus2 !== void 0 && _state$pushStatus2.pushedOn ? moment(state.pushStatus.pushedOn).format(CHIP_DATE_FORMAT) : '';
        return {
          label: pushedOn ? "Pushed ".concat(pushedOn) : 'Pushed',
          variant: 'success'
        };
      }
    case APPFOLIO_PUSH_STATUS.pending:
      return {
        label: 'Push requested',
        variant: 'info'
      };
    case APPFOLIO_PUSH_STATUS.failed:
      return {
        label: 'Failed',
        variant: 'error'
      };
    // Waiting never resolves this one — the invoice may hold two payables and a person has to look.
    case APPFOLIO_PUSH_STATUS.needsReconciliation:
      return {
        label: 'Needs reconciliation',
        variant: 'warning'
      };
    default:
      return {
        label: 'Not pushed',
        variant: 'inactive'
      };
  }
}

/**
 * Why the row's push action is inert. A disabled menu item hears no hover of its own, so the reason
 * hangs on a wrapper — and a state that cannot be pushed for a good reason should say which one.
 */
export function getAppfolioPushMenuHint(state) {
  var _state$pushStatus3;
  if (state.canPush) return null;
  if ((_state$pushStatus3 = state.pushStatus) !== null && _state$pushStatus3 !== void 0 && _state$pushStatus3.duplicateOfBillId) return 'This invoice was already pushed to AppFolio from another bill.';
  switch (state.status) {
    case APPFOLIO_PUSH_STATUS.succeeded:
      return 'This bill has already been pushed to AppFolio.';
    case APPFOLIO_PUSH_STATUS.pending:
      return 'This bill is on its way to AppFolio. The status will update here when it lands.';
    case APPFOLIO_PUSH_STATUS.needsReconciliation:
      return 'This invoice may hold two payables in AppFolio. Contact support to reconcile it.';
    default:
      return state.disabledReason;
  }
}

/**
 * Whether an AP push holds the bill against deletion — the service's rule, mirrored only as far as this
 * side can see it.
 *
 * The service refuses when a payable exists or may already: a row carrying an AppFolio bill id, or one
 * whose status is anything but Failed, Superseded or Blocked, or an unattributable `duplicate_reference`.
 * Of the document-level states, `succeeded`, `pending` and `needsReconciliation` always mean one of those
 * is true.
 *
 * `failed` is the one this cannot answer. It covers both an attempt blocked before dispatch — which the
 * service deliberately lets you delete, since refusing a push must not make a document undeletable — and
 * a real failure that may still hold a payable. So a failed document is offered, and the service has the
 * last word, which is the same bargain the allocation check already makes.
 */
export function isHeldByAppfolioPush(apPush) {
  return [APPFOLIO_PUSH_STATUS.succeeded, APPFOLIO_PUSH_STATUS.pending, APPFOLIO_PUSH_STATUS.needsReconciliation].includes(apPush === null || apPush === void 0 ? void 0 : apPush.status);
}

/**
 * Sort order for the column, worst news first so what needs attention sorts together. Ranked rather
 * than sorted by label, which would order the states alphabetically and mean nothing.
 */
const PUSH_SORT_RANK = {
  [APPFOLIO_PUSH_STATUS.needsReconciliation]: 0,
  [APPFOLIO_PUSH_STATUS.failed]: 1,
  [APPFOLIO_PUSH_STATUS.pending]: 2,
  [APPFOLIO_PUSH_STATUS.notPushed]: 3,
  [APPFOLIO_PUSH_STATUS.succeeded]: 4
};
export function getAppfolioPushSortRank(state) {
  var _state$pushStatus4, _PUSH_SORT_RANK$state;
  if (!(state !== null && state !== void 0 && state.hasReadStatus) || state.isUnknown) return 9;
  if ((_state$pushStatus4 = state.pushStatus) !== null && _state$pushStatus4 !== void 0 && _state$pushStatus4.duplicateOfBillId) return 5;
  return (_PUSH_SORT_RANK$state = PUSH_SORT_RANK[state.status]) !== null && _PUSH_SORT_RANK$state !== void 0 ? _PUSH_SORT_RANK$state : 3;
}